import clsx from 'clsx';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import Paper from '@mui/material/Paper';
import { getDataIdGenerator } from '../../../core/utils/generateDataId';
import HdIcon from '../../../media/components/icon/HdIcon';
import useDebounce from '../../../core/hooks/useDebounce';
import useDontMountAtFirst from '../../../core/hooks/useDontMountAtFirst';
import useInput from '../../hooks/useInput';
import { HdInputBase } from '../../../input/components/text-field/HdTextField';
import { HdIconButton } from '../../../components';
import styles from '../search-area/SearchArea.module.scss';

const QUERY_DEBOUNCE_TIME = 500;

interface SearchProps {
  isControlled?: boolean;
  value?: string;
  placeholder?: string;
  autofocus?: boolean;
  fullWidth?: boolean;
  hasBorder?: boolean;
  iconSize?: number;
  className?: string;
  filled?: boolean;
  disabled?: boolean;
  defaultExpanded?: boolean;
  debounceInterval?: number;
  collapsible?: boolean;
  defaultSearch?: string;
  onSearch?: (val: string) => void;
  onChange?: (val: string) => void;
  onCollapsedStateChange?: (state: boolean) => void;
  onBlur?: (e: any) => void;
  dataId: string;
}

/**
 * SearchAreaV2 gives choice to parent if it needs to
 * control the input.
 * */
export default function SearchAreaV2({
  isControlled = false,
  value,
  placeholder,
  autofocus,
  fullWidth = false,
  hasBorder = true,
  iconSize,
  className,
  filled,
  disabled,
  defaultExpanded = true,
  onSearch,
  debounceInterval = QUERY_DEBOUNCE_TIME,
  collapsible = true,
  defaultSearch = '',
  onChange,
  onCollapsedStateChange,
  onBlur,
  dataId
}: SearchProps) {
  const [expanded, setExpanded] = useState(collapsible ? defaultExpanded : true);
  const [internalValue, setInternalValue] = useInput(defaultSearch);
  const [isTyping, setIsTyping] = useState(false);

  const debouncedValue = useDebounce(internalValue, debounceInterval);

  const isStale = useRef(false);

  useDontMountAtFirst(() => {
    if (value !== internalValue) {
      isStale.current = true;
    }
  }, [value]);

  const finalValue = useMemo(() => {
    /**
     * Since we are keeping internalState and allowing parent component
     * to control the input, we need to make sure that we display the correct
     * finalValue to users. When parent tries to set determine the finalValue
     * at any point of time after mount, internalValue and value from parent will be
     * out of sync. For showing correct finalValue, we will check if it is stale, we need
     * to give precedence to value from parent. This will update the internalState as well, not immediately
     * but when first keystroke happens again.
     * */
    if (isStale.current) {
      isStale.current = false;
      return value;
    }

    /**
     * When user is typing some value, we want to show that value else user will see
     * delayed typed value.
     * */
    if (isTyping) {
      return internalValue;
    }

    if (isControlled) {
      return value;
    }

    return internalValue;
  }, [isTyping, internalValue, value]);

  useDontMountAtFirst(() => {
    if (onSearch) {
      onSearch(debouncedValue);
    }

    setIsTyping(false);
  }, [debouncedValue]);

  const onInternalValueChange = (e: any) => {
    setInternalValue(e);
    if (onChange) {
      onChange(e.target.value);
    }
  };

  const iconSizeFinal = iconSize || (collapsible ? 2 : 3);

  const inputRef = useRef(null);

  useDontMountAtFirst(() => {
    if (expanded) {
      inputRef.current?.focus();
    }
    if (onCollapsedStateChange) {
      onCollapsedStateChange(expanded);
    }
  }, [expanded]);

  const toggleExpand = () => {
    setExpanded(prev => !prev);
  };

  const clearSearch = (e: any) => {
    if (finalValue === '') {
      setExpanded(false);
    } else {
      inputRef.current?.focus();
    }
    setInternalValue(e, true);
  };

  const onKeyDown = (e: any) => {
    if (!isTyping) {
      setIsTyping(true);
    }
  };

  const dataIdGenerator = useCallback(getDataIdGenerator(dataId, 'search'), [dataId]);

  return (
    <div
      className={clsx(
        styles.searchWrapper,
        collapsible && expanded && styles.collapsible,
        expanded && fullWidth && styles.fullWidth,
        !hasBorder && styles.noBorder,
        filled && styles.filled
      )}
    >
      {expanded ? (
        <Paper
          className={`${styles.searchBox} ${collapsible && expanded ? styles.expanded : ''}`}
          sx={{ color: 'inherit' }}
          data-id='search-box'
        >
          <HdIcon name='search' size={iconSizeFinal} className={styles.icon} />

          <HdInputBase
            inputProps={{
              'aria-label': placeholder,
              'data-id': dataIdGenerator('input')
            }}
            inputRef={inputRef}
            className={className}
            autoFocus={autofocus}
            placeholder={placeholder}
            value={finalValue}
            disabled={disabled}
            onChange={e => onInternalValueChange(e)}
            onBlur={e => onBlur && onBlur(e)}
            onKeyDown={onKeyDown}
          />

          {finalValue?.length > 0 || collapsible === true ? (
            <HdIconButton
              aria-label='search'
              type='button'
              onClick={clearSearch}
              className={styles.actionButton}
              dataId={dataIdGenerator('clear')}
            >
              <HdIcon name='close' />
            </HdIconButton>
          ) : null}
        </Paper>
      ) : (
        <HdIconButton
          aria-label='search'
          type='button'
          onClick={toggleExpand}
          className={styles.actionButton}
          dataId={dataIdGenerator('toggle')}
        >
          <HdIcon name='search' size={3} />
        </HdIconButton>
      )}
    </div>
  );
}
